class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.header = document.querySelector('.section-header');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.allPredictiveSearch = document.querySelectorAll('.predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.recentSearchesKey = 'recentSearches';
    this.bodyClass = 'show-header-search';
    this.resizeTimeout;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onClick.bind(this));

    this.renderRecentSearches();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);


    if (!newSearchTerm.length) {
      this.querySelector('#predictive-search-results')?.remove();
      this.removeAttribute('results');
      return;
    } else if (this.searchTerm != newSearchTerm) {
      this.searchTerm = newSearchTerm;
      this.getSearchResults(this.searchTerm);
    } else if (!this.hasAttribute('results')) {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    } else {
      this.updateRecentSearch(this.getQuery());
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.removeAttribute('results');

      document.querySelector('predictive-search[open] .predictive-search').focus();
    }
  }

  onFocus() {
    if (!this.isOpen) {
      this.open();
    }

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    }
  } 

  onFocusOut() {
    let visualViewport = window.visualViewport?.height;
    let innerHeight = window.innerHeight;

    setTimeout(() => {
      if (document.documentElement.classList.contains('ios')) {
        if (visualViewport == innerHeight && !this.contains(document.activeElement)) {
          this.close();
        } else if (document.activeElement.tagName !== 'INPUT' || document.activeElement.tagName === 'INPUT' && !this.contains(document.activeElement)) {
          document.querySelector('predictive-search[open] .predictive-search').focus();
        }
      } else if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 200);
  }

  onResize() {
    clearTimeout(this.resizeTimeout);

    this.resizeTimeout = setTimeout(() => {
      this.currentWidth = window.innerWidth;

      if (this.currentWidth !== this.lastWidth) {
        this.lastWidth = this.currentWidth;

        if (this.isOpen) {
          this.close();
        }
      }
    }, 100);
  }

  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onClick(event) {
    const target = event.target;

    if (target.hasAttribute('data-search-value')) {
      const value = target.getAttribute('data-search-value');

      if (this.input) {
        this.input.value = value;
        this.onChange();
      }
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (!this.hasAttribute('results')) {
      this.allPredictiveSearch.forEach((el) => {
        el.classList.add('__loading-overlay');
      });
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[limit]=7&resources[limit_scope]=each&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    if (!this.isOpen) {
      this.open();
    }

    this.allPredictiveSearch.forEach((el) => {
      el.classList.remove('__loading-overlay');
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.input.closest('#menu-drawer')) {
      document.body.classList.add('show-header-search');      
    }

    setTimeout(() => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight * 0.01}px`);
    }, 100);

    if (document.documentElement.classList.contains('ios')) {
      document.addEventListener('touchmove', this.documentTouchmove);
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    document.body.classList.remove('show-header-search', '_header-mouseenter');

    setTimeout(() => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight * 0.01}px`);
    }, 100);
    
    document.removeEventListener('touchmove', this.documentTouchmove);
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  loadRecentSearches() {
    const data = localStorage.getItem(this.recentSearchesKey);
    return data ? JSON.parse(data) : {};
  }

  saveRecentSearches(searches) {
    localStorage.setItem(this.recentSearchesKey, JSON.stringify(searches));
  }

  updateRecentSearch(query) {
    const trimmed = query.trim();
    if (!trimmed) return;

    let searches = this.loadRecentSearches();
    if (!Array.isArray(searches)) searches = [];

    const last = searches[searches.length - 1];
    if (last && last.query === trimmed) {
      return;
    }

    searches.push({ query: trimmed });
    this.saveRecentSearches(searches);
  }

  renderRecentSearches() {
    const recentSearchesBlocks = document.querySelectorAll('[data-recent-searches]');
    const searches = this.loadRecentSearches();
    
    if (!Array.isArray(searches)) return;

    const sorted = searches.slice(-5).reverse();

    if (sorted.length > 0) {
      let html = '';

      sorted.forEach((item) => {
        html += `
          <li>
            <span class="link link__anim-show-only" data-search-value="${item.query}">${item.query}</span>
          </li>
        `;
      });

      recentSearchesBlocks.forEach((block, index) => {
        const list = block.querySelector('[data-recent-searches-list]');
        if (list) {
          list.innerHTML = html;
          block.classList.remove('hidden');
        }
      });
    }
  }

  documentTouchmove() {
    let activeElement = document.activeElement;

    if (activeElement && activeElement.tagName === 'INPUT') {
      //activeElement.blur();
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight * 0.01}px`);
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
